import React, { useMemo, useState } from "react";
import { Alert, Button, Select } from "antd";
import useLang from "../../../hooks/useLang";
import useBulkOrderOperation from "../../../hooks/useBulkOrderOperation";
import { setOrdersSectionId } from "../../../api/bulk-order-operations";
import {
	MAX_ORDER_IDS,
	SectionIdTargetCountry,
	getCategoryConfig,
} from "../../../utils/bulkOperationsConfig";
import { parseOrderIds } from "../parseOrderIds";
import BulkOpsHeader from "../components/BulkOpsHeader";
import ConfirmModal from "../components/ConfirmModal";
import OrderIdsInput from "../components/OrderIdsInput";
import ResultPanels from "../components/ResultPanels";

const SectionIdCategory = () => {
	const { strings } = useLang("bulkOrderOperations");
	const common = strings?.common;
	const copy = strings?.sectionId;
	const categoryStrings = strings?.categories?.sectionId;

	const categoryConfig = getCategoryConfig("section-id");

	const [countryId, setCountryId] = useState<number | null>(null);
	const [rawInput, setRawInput] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmWord, setConfirmWord] = useState("");

	const { submitting, result, errorResult, run, clearResults } =
		useBulkOrderOperation();

	const parsed = useMemo(() => parseOrderIds(rawInput), [rawInput]);
	const tooManyIds = parsed.valid.length > MAX_ORDER_IDS;

	const countryLabel = (country: SectionIdTargetCountry) =>
		copy?.countries?.[country.labelKey] ?? country.fallbackLabel;

	const selectedCountry =
		categoryConfig?.countries.find((c) => c.countryId === countryId) ??
		null;

	// A country left at sectionId 0 is one nobody has filled in yet. Offering it would
	// send a value the API rejects, so it is shown disabled with its id visible.
	const countryOptions = (categoryConfig?.countries ?? []).map((country) => ({
		value: country.countryId,
		label: countryLabel(country),
		disabled: country.sectionId <= 0,
	}));

	const canSubmit =
		!!selectedCountry &&
		selectedCountry.sectionId > 0 &&
		parsed.valid.length > 0 &&
		!tooManyIds &&
		!submitting;

	const closeConfirm = () => {
		setConfirmOpen(false);
		setConfirmWord("");
	};

	// A result belongs to one (country, id list) pair — changing either makes the
	// panel below describe a batch that is no longer the one on screen.
	const selectCountry = (value: number) => {
		setCountryId(value);
		clearResults();
	};

	const changeInput = (value: string) => {
		setRawInput(value);
		clearResults();
	};

	const handleConfirm = async () => {
		if (!selectedCountry || selectedCountry.sectionId <= 0) return;

		await run(() =>
			setOrdersSectionId({
				orderIds: parsed.valid,
				sectionId: selectedCountry.sectionId,
			})
		);

		closeConfirm();
	};

	const targetLabel = selectedCountry
		? `${countryLabel(selectedCountry)} — ${
				selectedCountry.sectionName || "?"
		  } (${selectedCountry.sectionId})`
		: "—";

	return (
		<div className="max-w-5xl mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={categoryStrings?.label ?? "Section ID"}
				subtitle={
					categoryStrings?.description ??
					"Move orders registered against the wrong country to the right zone"
				}
				backTo="/bulk-order-operations"
				backLabel={common?.back ?? "All categories"}
			/>

			{!categoryConfig && (
				<Alert
					type="error"
					showIcon
					message={
						common?.categoryUnavailable ??
						"This category is not configured for this country."
					}
				/>
			)}

			<div>
				<div className="font-medium mb-2">
					{copy?.countryLabel ?? "Target country"}
				</div>
				<Select
					className="w-full max-w-md"
					value={countryId}
					onChange={selectCountry}
					placeholder={copy?.selectCountry ?? "Select a country"}
					options={countryOptions}
				/>
				{selectedCountry && (
					<div className="text-xs text-gray-500 mt-1">
						{copy?.targetSection ?? "Orders will be set to zone"}:{" "}
						<b>
							{selectedCountry.sectionName || "?"} (
							{selectedCountry.sectionId})
						</b>
					</div>
				)}
			</div>

			{/*
				The id list and the button mean nothing until a target is chosen — there is
				no default country, deliberately: picking the wrong one re-homes orders.
			*/}
			{!selectedCountry ? (
				<Alert
					type="info"
					showIcon
					message={
						copy?.selectCountryPrompt ??
						"Select a target country to continue"
					}
				/>
			) : (
				<>
					<Alert
						type="warning"
						showIcon
						message={
							copy?.scopeNote ??
							"Only the order's zone (SectionId) is changed. Status, balance and history are left exactly as they are."
						}
					/>

					<OrderIdsInput
						value={rawInput}
						onChange={changeInput}
						parsed={parsed}
						tooManyIds={tooManyIds}
					/>

					<div>
						<Button
							size="large"
							danger
							type="primary"
							disabled={!canSubmit}
							onClick={() => setConfirmOpen(true)}
						>
							{common?.submit ?? "Execute operation"}
						</Button>
					</div>
				</>
			)}

			<ConfirmModal
				open={confirmOpen}
				operationLabel={categoryStrings?.label ?? "Section ID"}
				orderCount={parsed.valid.length}
				detail={{
					label: copy?.confirmTarget ?? "Target zone",
					value: targetLabel,
				}}
				danger
				dangerWarning={
					copy?.dangerWarning ??
					"This changes which country these orders belong to. It affects order lists, driver visibility and reports, and the previous zone is only recorded in the result below."
				}
				confirmWord={confirmWord}
				onConfirmWordChange={setConfirmWord}
				submitting={submitting}
				onOk={handleConfirm}
				onCancel={closeConfirm}
			/>

			<ResultPanels result={result} errorResult={errorResult} />
		</div>
	);
};

export default SectionIdCategory;
